/**
 * @prettier
 */

import React, { FunctionComponent } from "react";
import { Router, RouteComponentProps, Redirect } from "@reach/router";
import App from "App";
import { UserProvider } from "UserContext";
import { UnsavedProvider } from "LinkContext";
import Dashboard from "containers/Dashboard";
import NotFound from "components/NotFound";
import ErrorBoundary from "components/ErrorBoundary";
import PrivateRoute from "components/hoc/PrivateRoute";
import PublicRoute from "components/hoc/PublicRoute";
import Expired from "components/Public/Expired";
import Invalid from "components/Public/Invalid";
import { PrimaryUserProvider } from "PrimaryUserContext";
import { AuthUserProvider } from "AuthUserContext";
import { lazyPage, named } from "utils/lazyPage";

// pages load on first visit (see utils/lazyPage)
const AuthPage = lazyPage(() => import("containers/AuthPage"));
const BeMeasuredPage = lazyPage(() => import("containers/BeMeasured"));
// import BePlannedPage from "containers/BePlanned";
const BePlannedMonthly = lazyPage(() => import("containers/BePlannedMonthly"));
const Privacy = lazyPage(() => import("containers/Privacy"));
const OrganicCampaigns = lazyPage(() => import("containers/BeAmplified/Campaigns/Organic"));
const CampaignPage = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/ViewCampaign/AutoFollowerCampaignPage")
);
const CreateCampaign = lazyPage(() =>
  import("components/BeAmplified/Campaigns/Organic/Create/AutoFollow/SharedCreateCampaign").then(
    named("CreateCampaign")
  )
);
const CreateAutoDm = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/CreateCampaign/AutoDm").then(
    named("CreateAutoDm")
  )
);
const AutoPosterCampaignPage = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/ViewCampaign/AutoPosterCampaignPage")
);
const CreateAutoPoster = lazyPage(() =>
  import("components/BeAmplified/Campaigns/Organic/Create/AutoPoster/Twitter").then(
    named("CreateAutoPoster")
  )
);
const AutoDmCampaignPage = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/ViewCampaign/AutoDmCampaignPage")
);
const AutoLikerCampaignPage = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/ViewCampaign/AutoLikerCampaignPage")
);
const GroupExtractorPage = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/ViewCampaign/GroupExtractorPage")
);
const GroupExtractor = lazyPage(() =>
  import("components/BeAmplified/Campaigns/Organic/Create/GroupExtractor/Facebook").then(
    named("GroupExtractor")
  )
);
const CreateAutoLiker = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/CreateCampaign/AutoLiker").then(
    named("CreateAutoLiker")
  )
);
const CreateOrganicCampaign = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/CreateCampaign/AutoFollow").then(
    named("CreateOrganicCampaign")
  )
);
const AutoCommenterCampaignPage = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/ViewCampaign/AutoCommenterCampaignPage")
);
const CreateAutoCommenter = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/CreateCampaign/AutoCommenter").then(
    named("CreateAutoCommenter")
  )
);
const ContentPlannerFeed = lazyPage(() =>
  import("containers/BeplannedV2/ContentPlannerData/ContentPlannerFeed").then(
    named("ContentPlannerFeed")
  )
);
const ClientInfoPage = lazyPage(() => import("containers/ClientInfo"));

export const AppRouter: FunctionComponent<RouteComponentProps> = () => (
  <ErrorBoundary>
    <AuthUserProvider>
      <PrimaryUserProvider>
        <UserProvider>
          <UnsavedProvider>
            <Router>
              <App path="/" location={{ pathname: "/" }}>
                <AuthPage path="sign-in" />
                <AuthPage path="forgot-password" />
                <AuthPage path="sign-up" />
                <Privacy path="privacy-policy" />
                <NotFound default />
                <Dashboard path="/">
                  {!window.location.pathname.includes("/public") ? (
                    <PrivateRoute path="/">
                      <BeMeasuredPage path="be-measured" />
                      <Redirect
                        from="be-planned-feed"
                        to="/content-plan-feed"
                        noThrow
                      />
                      <ClientInfoPage path="client-info/:uid" />

                      {/* <BePlannedPage path="content-plan-feed" /> */}
                      <ContentPlannerFeed path="content-plan-feed" />
                      <Redirect
                        from="be-planned-monthly"
                        to="/monthly-conversation-planner"
                        noThrow
                      />
                      <BePlannedMonthly path="monthly-conversation-planner" />
                      {/* this needs refactoring */}
                      <CreateOrganicCampaign path="campaigns/organic/create" />
                      <CreateCampaign path="campaigns/organic/:uid/edit" />
                      <CreateCampaign path="campaigns/organic/:platform/:uid/edit" />
                      <CreateAutoDm path="campaigns/organic/create/auto-dm" />
                      <CreateAutoDm path="campaigns/organic/create/:platform/auto-dm" />
                      <CreateAutoDm path="campaigns/organic/create/auto-dm/:uid/edit" />
                      <CreateAutoDm path="campaigns/organic/create/:platform/auto-dm/:uid/edit" />
                      <CreateAutoPoster path="campaigns/organic/create/auto-poster" />
                      <CreateAutoLiker path="campaigns/organic/create/:platform/auto-liker" />
                      <CreateAutoCommenter path="campaigns/organic/create/:platform/auto-commenter" />

                      {/* Hide For now */}
                      {/* <BePlannedGrid path="be-planned-grid" /> */}
                      <OrganicCampaigns path="campaigns/organic" />
                      <CampaignPage path="campaigns/organic/:uid" />
                      <CampaignPage path="campaigns/organic/:platform/:uid" />
                      <AutoPosterCampaignPage path="campaigns/organic/autoposter/:uid" />
                      <AutoDmCampaignPage path="campaigns/organic/auto-dm/:uid" />
                      <AutoDmCampaignPage path="campaigns/organic/:platform/auto-dm/:uid" />
                      <AutoLikerCampaignPage path="campaigns/organic/auto-liker/:platform/:uid" />
                      <AutoCommenterCampaignPage path="campaigns/organic/auto-commenter/:platform/:uid" />
                      <GroupExtractor path="campaigns/organic/create/extractor" />
                      <GroupExtractorPage path="campaigns/organic/group-extractor/:platform/:uid" />
                    </PrivateRoute>
                  ) : (
                    <PublicRoute path="/public">
                      <BeMeasuredPage path="be-measured/:userCipher" />
                      {/* <BePlannedPage path="content-plan-feed/:userCipher" /> */}
                      <ContentPlannerFeed path="content-plan-feed/:userCipher" />
                      <BePlannedMonthly path="monthly-conversation-planner/:userCipher" />
                      <Expired path="expired" />
                      <Invalid path="invalid" />
                    </PublicRoute>
                  )}
                  <NotFound default />
                </Dashboard>
              </App>
            </Router>
          </UnsavedProvider>
        </UserProvider>
      </PrimaryUserProvider>
    </AuthUserProvider>
  </ErrorBoundary>
);
